import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, Star } from "lucide-react";
import { testimonials, WA_DEMO } from "../../lib/site";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

export const Testimonials = () => {
  const [[index, dir], setIndex] = useState([0, 0]);
  const reduce = useReducedMotion();
  const t = testimonials[index];

  const go = (d) => setIndex(([i]) => [(i + d + testimonials.length) % testimonials.length, d]);

  useEffect(() => {
    const id = setInterval(() => go(1), 6500);
    return () => clearInterval(id);
  }, [index]);

  return (
    <section data-testid="testimonials-section" className="relative py-24 sm:py-32 bg-panel/30 border-y hairline overflow-hidden" aria-label="Testimonials">
      <div className="glow-orb w-[600px] h-[600px] -bottom-52 left-1/3 opacity-50" />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 relative">
        <SectionHeading
          eyebrow="Client Words"
          lines={[<>Don't take my word.</>, <>Take <em className="italic text-brand">theirs.</em></>]}
          align="center"
          className="mb-14"
        />

        <Reveal>
          <div className="relative border hairline bg-ink p-8 sm:p-14 min-h-[300px] flex flex-col justify-between" data-testid="testimonial-card">
            <div className="flex gap-1 mb-8" aria-label="5 star rating">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} className="fill-brand text-brand" />
              ))}
            </div>
            <AnimatePresence mode="wait" custom={dir}>
              <motion.blockquote
                key={index}
                custom={dir}
                initial={reduce ? { opacity: 0 } : { opacity: 0, x: dir >= 0 ? 60 : -60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, x: dir >= 0 ? -60 : 60 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="font-display font-light text-2xl sm:text-3xl lg:text-4xl text-white leading-snug" data-testid="testimonial-quote">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <footer className="mt-8">
                  <p className="text-white font-semibold" data-testid="testimonial-name">{t.name}</p>
                  <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-brand mt-1" data-testid="testimonial-role">{t.role}</p>
                </footer>
              </motion.blockquote>
            </AnimatePresence>

            <div className="flex items-center justify-between mt-10 pt-6 border-t hairline">
              <div className="flex gap-2">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`testimonial-dot-${i}`}
                    onClick={() => setIndex([i, i > index ? 1 : -1])}
                    aria-label={`Go to testimonial ${i + 1}`}
                    className={`h-1.5 transition-all duration-500 ${i === index ? "w-8 bg-brand" : "w-3 bg-white/20 hover:bg-white/40"}`}
                  />
                ))}
              </div>
              <div className="flex gap-2">
                <button data-testid="testimonial-prev-button" onClick={() => go(-1)} aria-label="Previous testimonial" className="btn-arrow-box !w-11 !h-11"><ArrowLeft size={16} /></button>
                <button data-testid="testimonial-next-button" onClick={() => go(1)} aria-label="Next testimonial" className="btn-arrow-box !w-11 !h-11"><ArrowRight size={16} /></button>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="mt-8 text-center text-mute text-sm" data-testid="testimonials-cta-line">
            Join them — <a href={WA_DEMO} target="_blank" rel="noopener noreferrer" className="text-brand underline underline-offset-4 decoration-brand/40 hover:decoration-brand">start with a free demo</a>.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
